import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { MapPin, Target, Award, Users, Calendar, TrendingUp, Loader2 } from 'lucide-react';
import SiteHeader from '@/components/SiteHeader';
import Footer from '@/components/Footer';
import SEO from '@/components/SEO';
import axios from 'axios';
import { API } from '@/App';

const AboutPage = () => {
  const navigate = useNavigate();
  const [about, setAbout] = useState(null);
  const [loading, setLoading] = useState(true);
  const [counts, setCounts] = useState({ members: 0, formations: 0 });

  useEffect(() => {
    const fetchAbout = async () => {
      try {
        const [aboutRes, usersRes, formationsRes] = await Promise.all([
          axios.get(`${API}/site-content/about`),
          axios.get(`${API}/directory`).catch(() => ({ data: [] })),
          axios.get(`${API}/formations/public`).catch(() => ({ data: [] })),
        ]);
        if (aboutRes.data?.data) setAbout(aboutRes.data.data);
        const dirs = Array.isArray(usersRes.data) ? usersRes.data : (usersRes.data?.practitioners || []);
        const forms = Array.isArray(formationsRes.data) ? formationsRes.data : [];
        setCounts({ members: dirs.length, formations: forms.length });
      } catch { /* fallback */ }
      setLoading(false);
    };
    fetchAbout();
  }, []);

  const stats = [
    { icon: MapPin, number: about?.stat_regions || '13', label: 'Régions representees', bg: 'bg-[#eaf6fc]', text: 'text-[#35AFE4]' },
    { icon: Calendar, number: about?.stat_years ? `${about.stat_years}+` : '20+', label: "Ans d'existence", bg: 'bg-[#eaf6fc]', text: 'text-[#35AFE4]' },
    { icon: Users, number: counts.members > 0 ? `${counts.members}+` : '500+', label: 'Membres actifs', bg: 'bg-[#e8f5e9]', text: 'text-[#2e7d32]' },
    { icon: TrendingUp, number: counts.formations > 0 ? `${counts.formations}+` : '100+', label: 'Formations disponibles', bg: 'bg-[#fff3e0]', text: 'text-[#e65100]' }
  ];

  const values = [
    {
      icon: Target,
      title: 'Notre Mission',
      description: "Promouvoir l'excellence en contactologie et soutenir la formation continue des professionnels de sante visuelle."
    },
    {
      icon: Award,
      title: 'Excellence',
      description: "Garantir les plus hauts standards de qualité dans l'adaptation des lentilles de contact et les soins aux patients."
    },
    {
      icon: Users,
      title: 'Communaute',
      description: "Rassembler les ophtalmologistes adaptateurs de lentilles de contact pour partager les connaissances et les bonnes pratiques."
    }
  ];

  return (
    <div className="min-h-screen bg-white" style={{ fontFamily: "'Roboto', sans-serif" }}>
      <SiteHeader />
      <SEO title="Qui sommes-nous" description="La SFOALC est une association qui regroupe les ophtalmologistes adaptateurs de lentilles de contact." />
      <div className="h-[72px]" />

      {/* Hero Section */}
      <section className="pt-12 pb-16 px-4" style={{ background: '#eaf6fc' }}>
        <div className="max-w-4xl mx-auto text-center">
          <h1 className="text-4xl sm:text-5xl font-bold text-gray-900 mb-6" style={{ fontFamily: "'Barlow', sans-serif" }} data-testid="about-title">
            {about?.title || 'Qui sommes-nous ?'}
          </h1>
          {loading ? (
            <div className="flex justify-center py-8"><Loader2 className="animate-spin text-[#35AFE4]" size={28} /></div>
          ) : (
            <div className="space-y-4 text-lg text-gray-700 leading-relaxed text-left">
              {(about?.paragraphs || [
                "La SFOALC est une association créée dans les années 2000 qui regroupe l'ensemble des ophtalmologistes qui pratiquent la contactologie médicale.",
                "Forte de son ancienneté et de son expérience, la SFOALC a souhaité se mettre à votre service avec ce nouveau site internet afin de faciliter votre pratique de la contactologie médicale."
              ]).map((p, i) => (
                <p key={i}>{p}</p>
              ))}
            </div>
          )}
          {about?.closing && (
            <p className="mt-8 text-xl font-semibold text-[#1e3a5f] italic">
              {about.closing}
            </p>
          )}
        </div>
      </section>

      {/* Stats */}
      <section className="py-16 px-4 bg-white">
        <div className="max-w-5xl mx-auto grid grid-cols-2 md:grid-cols-4 gap-6">
          {stats.map((stat, i) => {
            const Icon = stat.icon;
            return (
              <Card key={i} className="text-center border-0 shadow-none hover:shadow-md transition-all" data-testid={`about-stat-${i}`}>
                <CardContent className="pt-6 pb-4">
                  <div className={`w-14 h-14 rounded-2xl ${stat.bg} flex items-center justify-center mx-auto mb-4`}>
                    <Icon className={stat.text} size={24} />
                  </div>
                  <p className="text-3xl font-bold text-gray-900 mb-1">{stat.number}</p>
                  <p className="text-sm text-gray-500">{stat.label}</p>
                </CardContent>
              </Card>
            );
          })}
        </div>
      </section>

      {/* Values */}
      <section className="py-16 px-4 bg-gray-50">
        <div className="max-w-5xl mx-auto">
          <h2 className="text-2xl font-bold text-gray-900 text-center mb-10" style={{ fontFamily: "'Barlow', sans-serif" }}>
            Nos Valeurs
          </h2>
          <div className="grid md:grid-cols-3 gap-8">
            {values.map((val, i) => {
              const Icon = val.icon;
              return (
                <div key={i} className="text-center" data-testid={`about-value-${i}`}>
                  <div className="w-16 h-16 rounded-full bg-[#1e3a5f] flex items-center justify-center mx-auto mb-5">
                    <Icon className="text-white" size={28} />
                  </div>
                  <h3 className="text-lg font-bold text-gray-900 mb-3">{val.title}</h3>
                  <p className="text-gray-600 leading-relaxed">{val.description}</p>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-16 px-4 bg-[#1e3a5f] text-white text-center">
        <div className="max-w-3xl mx-auto">
          <h2 className="text-2xl font-bold mb-4" style={{ fontFamily: "'Barlow', sans-serif" }}>
            Rejoignez la SFOALC
          </h2>
          <p className="text-white/80 mb-8">
            Devenez membre et accédez a toutes nos ressources, formations et événements.
          </p>
          <Button onClick={() => navigate('/adhesion')} className="bg-white text-[#1e3a5f] hover:bg-gray-100 font-bold px-8 py-3 rounded-xl" data-testid="about-cta-btn">
            Devenir membre
          </Button>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default AboutPage;
